import { agentDraftSchema, isValidDomainPattern, LIMITS, type AgentDraft } from "@nemo/agent-schema";

export type FieldErrors = Record<string, string>;

/** Same wording as the API's friendly messages, so a field says the same thing before and after saving. */
export function friendly(message: string): string {
  if (/received undefined/.test(message)) return "This field is required.";
  if (/expected string to have >=1 characters/.test(message)) return "This field cannot be empty.";
  const max = /expected string to have <=(\d+) characters/.exec(message)?.[1];
  if (max) return `Keep this under ${max} characters.`;
  if (/Invalid email/i.test(message)) return "Enter a valid email address.";
  if (/Invalid URL/i.test(message)) return "Enter a valid URL, starting with https://";
  if (/expected array to have <=(\d+) items/.test(message)) return `You can add up to ${/<=(\d+)/.exec(message)?.[1]}.`;
  return message;
}

/** Validates the form as it is. Messages are shown next to the field named by the path. */
export function validate(form: AgentDraft): FieldErrors {
  const out: FieldErrors = {};
  const r = agentDraftSchema.safeParse(form);
  if (!r.success) for (const i of r.error.issues) out[i.path.join(".")] ??= friendly(i.message);
  const domains = form.deployment?.website?.allowedDomains ?? [];
  domains.forEach((d, idx) => {
    if (!isValidDomainPattern(d)) {
      out[`deployment.website.allowedDomains.${idx}`] = /^\*/.test(d.replace(/^[a-z]+:\/\//i, ""))
        ? "Use a wildcard like *.example.com. Entries such as * or *.com would allow any website."
        : "Enter a domain like example.com or *.example.com.";
    }
  });
  return out;
}

function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

function removeAt(root: unknown, path: (string | number)[]): void {
  let node = root as Record<string | number, unknown>;
  for (let i = 0; i < path.length - 1; i++) {
    const next = node?.[path[i] as string];
    if (next === undefined || next === null) return;
    node = next as Record<string | number, unknown>;
  }
  const last = path[path.length - 1] as string | number;
  if (Array.isArray(node)) node.splice(Number(last), 1);
  else delete node[last];
}

/**
 * What can be saved right now. Empty strings and half-filled rows (a FAQ with no answer yet) are left out,
 * and so is any value the schema rejects. The form itself is never changed: what the person typed stays on
 * screen with its error, and is sent as soon as it is valid.
 */
export function toDraft(form: AgentDraft): AgentDraft {
  const d = clone(form) as Record<string, any>;
  for (const k of ["name", "creatorName", "avatarUrl", "persona", "responseInstructions"]) if (typeof d[k] === "string" && d[k].trim() === "") delete d[k];
  for (const k of ["greetings", "conversationStarters", "additionalEmails"]) if (Array.isArray(d[k])) d[k] = d[k].filter((s: string) => s.trim() !== "");
  // The older single notice becomes notice 1 of the list; empty notices are left out (what was typed stays on screen).
  if (d.legalNotice && !d.legalNotices) d.legalNotices = [d.legalNotice];
  delete d.legalNotice;
  if (Array.isArray(d.legalNotices)) d.legalNotices = d.legalNotices.filter((n: { value?: string }) => String(n?.value ?? "").trim() !== "");
  const k = d.knowledge;
  if (k?.faqs) k.faqs = k.faqs.filter((f: { question?: string; answer?: string }) => f.question?.trim() && f.answer?.trim());
  if (k?.topics) {
    k.topics = k.topics
      .map((t: { title?: string; triggers?: string[]; answer?: string }) => ({ ...t, triggers: (t.triggers ?? []).filter((x) => x.trim() !== "") }))
      .filter((t: { title?: string; triggers: string[]; answer?: string }) => t.title?.trim() && t.triggers.length > 0 && t.answer?.trim());
  }
  const web = d.deployment?.website;
  if (web) {
    web.allowedDomains = (web.allowedDomains ?? []).filter((x: string) => x.trim() !== "");
    for (const f of ["fontFamily", "brandName"]) if (typeof web[f] === "string" && web[f].trim() === "") delete web[f];
  }
  const lc = d.leadCapture;
  if (lc) {
    lc.fields = (lc.fields ?? []).filter((f: { key?: string; label?: string }) => f.key && f.label?.trim());
    lc.notifyEmails = (lc.notifyEmails ?? []).filter((s: string) => s.trim() !== "");
    if (lc.crm && !Object.values(lc.crm).some((v) => v === "" )) {
      /* complete */
    } else if (lc.crm) delete lc.crm;
  }
  for (let round = 0; round < 8; round++) {
    const r = agentDraftSchema.safeParse(d);
    if (r.success) return r.data;
    // remove from the end so array indexes stay valid
    const paths = r.error.issues.map((i) => i.path as (string | number)[]).sort((a, b) => b.length - a.length || Number(b[b.length - 1]) - Number(a[a.length - 1]));
    for (const p of paths) removeAt(d, p);
  }
  return {};
}

export const isValidDomain = isValidDomainPattern;
export { LIMITS };

/** Stable fingerprint to see whether anything changed since the last save. */
export const fingerprint = (d: AgentDraft) => JSON.stringify(d);

/** Fields that publishing needs, in the order the wizard shows them, for the "what is missing" list. */
export const REQUIRED_LABELS: Record<string, string> = {
  name: "Agent name",
  creatorName: "Creator name",
  persona: "Job, skills and personality",
  responseInstructions: "Response instructions",
  greetings: "Greeting message",
  "model.generative": "Generative model",
  "model.embedding": "Embedding model",
  "deployment.website.allowedDomains": "Allowed domains",
};
